import React, { useEffect, useState, useRef } from "react";
import { isSet, calculateAge, locations, formatPhoneNumberUS } from "../../../utils/commonUtils";
import FormInputs from "../../../components/UI/FormInputs";
import { EthinicityOptions, ItentificationTypeOptions, RelationShipOptions, SOCIAL_SECURITY_NUMBER_FORMAT, genderOptions, raceOptions, isDotDriver } from "../../../utils/constant";
import InsuranceService from "../../../services/InsuranceService";
import DropDownService from "../../../services/DropDownService";
import DivisionService from "../../../services/DivisionService";

// Canadian province codes to filter out (only show US states)
const canadianProvinces = ['AB', 'BC', 'MB', 'NB', 'NL', 'NT', 'NS', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'];

// State options (US states only) - both label and value are state abbreviations
const stateOptions = [
  { label: "-Select State-", value: "" },
  ...Object.keys(locations)
    .filter(key => !canadianProvinces.includes(key))
    .map(key => ({
      label: key,
      value: key
    }))
];

const DemographicsVerification = (props) => {
    const { patientFormData, formData, onChangeHandler, validation, bulkOnChangeHandler } = props;
    const employee = patientFormData?.employee || {};
    const company = patientFormData?.company || {};
    const organization = patientFormData?.organization || {};
    const [insuranceListOptions, setInsuranceListOptions] = useState([{label:'-Select-',value:''}]);
    const [employeeListOptions, setEmployeeListOptions] = useState([{label:'-Select-',value:''}]);
    const [companyListOptions, setCompanyListOptions] = useState([{label:'-Select-',value:''}]);
    const [clinicListOptions, setClinicListOptions] = useState([{label:'-Select-',value:''}]);
    const [clinicDepartmentListOptions, setClinicDepartmentListOptions] = useState([{label:'-Select-',value:''}]);
    const [divisionListOptions, setDivisionListOptions] = useState([{label:'-Select-',value:''}]);
    const [division2ListOptions, setDivision2ListOptions] = useState([{label:'-Select-',value:''}]);
    const sectionRefs = useRef({});
    const verificationSectionRef = useRef(null);

    // Fetch insurance list (public route, no authentication required)
    useEffect(() => {
        const fetchInsuranceList = async () => {
            try {
                let options = [{label:'-Select-',value:''}];
                const res = await InsuranceService.getInsuranceListForPatientForm();
                const resData = res.data;
                // Response structure: data.data.data (array of insurance companies)
                if (resData && resData.data && resData.data.data && Array.isArray(resData.data.data)) {
                    resData.data.data.forEach((item) => {
                        options.push({
                            label: item.insurance_name,
                            value: item.insurance_company_id
                        });
                    });
                }
                setInsuranceListOptions(options);
            } catch (e) {
                console.error('Error fetching insurance list:', e);
            }
        };
        fetchInsuranceList();
    }, []);

    // Fetch employee list when is_dependent is checked (public route, no authentication required)
    useEffect(() => {
        const fetchEmployeeList = async () => {
            if (formData.is_dependent == 1 && organization?.organization_uuid) {
                try {
                    let options = [{label:'-Select-',value:''}];
                    const response = await DropDownService.getEmployeeListForPatientForm(organization.organization_uuid);
                    if (response && Array.isArray(response)) {
                        response.forEach((item) => {
                            // Exclude current employee from the list
                            if (item.employee_id !== employee?.employee_id) {
                                options.push({
                                    label: `${item.first_name} ${item.last_name} - ${formatPhoneNumberUS(item.phone)}`,
                                    value: item.employee_id
                                });
                            }
                        });
                    }
                    setEmployeeListOptions(options);
                } catch (e) {
                    console.error('Error fetching employee list:', e);
                }
            } else {
                // Reset to default when not dependent
                setEmployeeListOptions([{label:'-Select-',value:''}]);
            }
        };
        fetchEmployeeList();
    }, [formData.is_dependent, organization?.organization_uuid, employee?.employee_id]);

    // Initialize form data with employee data if not already set
    useEffect(() => {
        if (employee && Object.keys(formData).length === 0) {
            const initialData = {
                first_name: employee.first_name || '',
                last_name: employee.last_name || '',
                gender: employee.gender || '',
                dob: employee.dob || '',
                phone: employee.phone || '',
                email: employee.email || '',
                street_address: employee.street_address || employee.address || '',
                city: employee.city || '',
                state: employee.state || '',
                county: employee.county || '',
                zip_code: employee.zip_code || '',
                social_security_number: employee.social_security_number || '',
                ethnicity: employee.ethnicity || '',
                race: employee.race || '',
                company_id: company?.company_uuid || '',
                clinic_id: employee.clinic_id || '',
                clinic_department_id: employee.clinic_department_id || '',
                division_id: employee.division_id || '',
                division2_id: employee.division2_id || '',
                insurance_id: employee.insurance_id || '',
                member_number: employee.member_number || '',
                group_number: employee.group_number || '',
                patient_id: employee.patient_id || '',
                booking_id: employee.booking_id || '',
                identification_type: employee.identification_type || '',
                identification_number: employee.identification_number || '',
                guardian_name: employee.guardian_name || '',
                guardian_phone: employee.guardian_phone || '',
                is_dependent: employee.is_dependent || 0,
                dependent_id: employee.dependent_id || '',
                dependent_relationship: employee.dependent_relationship || '',
                status: employee.status || 1,
                job_title_description: employee.job_title_description || '',
                start_date: employee.start_date || '',
                employee_relationship: employee.employee_relationship || '',
                reporting_supervisor: employee.reporting_supervisor || '',
                benefit_type: employee.benefit_type || '',
                license_certification_description: employee.license_certification_description || '',
                license_effective_date: employee.license_effective_date || '',
                license_expiration_date: employee.license_expiration_date || '',
                is_dot_driver: employee.is_dot_driver || '',
                license_issuing_state: employee.license_issuing_state || '',
                disable_labs_drugscreen_notification: employee.disable_labs_drugscreen_notification || 0,
                demographics_verified: false,
                // Initialize file fields with proper structure
            };
            bulkOnChangeHandler(initialData);
        }
    }, [employee]);

    // Fetch companies list on mount (matching PatientDetails.js)
    useEffect(() => {
        const fetchCompaniesList = async () => {
            if (organization?.organization_uuid) {
                try {
                    const response = await DropDownService.getCompaniesListForPatientForm(organization.organization_uuid);
                    if (response && Array.isArray(response)) {
                        let options = [{label:'-Select-',value:''}];
                        response.forEach((company) => {
                            options.push({
                                label: company.name,
                                value: company.company_uuid
                            });
                        });
                        setCompanyListOptions(options);
                    }
                } catch (e) {
                    console.error('Error fetching companies list:', e);
                }
            }
        };
        fetchCompaniesList();
    }, [organization?.organization_uuid]);

    // Fetch clinics when company_id changes (matching PatientDetails.js)
    useEffect(() => {
        const fetchClinicDetails = async () => {
            if (formData.company_id && organization?.organization_uuid) {
                try {
                    let options = [{label:'-Select Clinic',value:''}];
                    const data = await DropDownService.getClinicsByCompanyForPatientForm(formData.company_id, organization.organization_uuid);
                    if (data && Array.isArray(data)) {
                        data.forEach(item => {
                            options.push({label: item.name, value: item.clinic_id});
                        });
                    }
                    setClinicListOptions(options);
                } catch (e) {
                    console.error('Error fetching clinics:', e);
                }
            } else {
                setClinicListOptions([{label:'-Select Clinic',value:''}]);
            }
        };
        fetchClinicDetails();
    }, [formData.company_id, organization?.organization_uuid]);

    // Fetch clinic departments when clinic_id changes (matching PatientDetails.js)
    useEffect(() => {
        const fetchClinicLocationList = async () => {
            if (formData.clinic_id) {
                try {
                    const data = await DropDownService.getClinicDepartmentsByClinicForPatientForm(formData.clinic_id);
                    let clinicLocationInArr = [{label:'-Select-',value:''}];
                    if (data && Array.isArray(data)) {
                        data.forEach((item, itemIndex) => {
                            clinicLocationInArr.push({
                                label: item.facility_name,
                                value: item.id,
                                key: itemIndex
                            });
                        });
                    }
                    setClinicDepartmentListOptions(clinicLocationInArr);
                } catch (e) {
                    console.error('Error fetching clinic departments:', e);
                }
            } else {
                setClinicDepartmentListOptions([{label:'-Select-',value:''}]);
            }
        };
        fetchClinicLocationList();
    }, [formData.clinic_id]);

    // Fetch divisions when company_id changes (matching PatientDetails.js)
    useEffect(() => {
        const fetchDivisionList = async () => {
            if (formData.company_id) {
                try {
                    let options = [{label:'-Select-',value:''}];
                    const res = await DivisionService.getDivisionByCompanyId(formData.company_id);
                    if (res && Array.isArray(res)) {
                        res.forEach((item) => {
                            options.push({
                                label: item.division_name,
                                value: item.division_id
                            });
                        });
                    }
                    setDivisionListOptions(options);
                } catch (e) {
                    console.error('Error fetching divisions:', e);
                }
            } else {
                setDivisionListOptions([{label:'-Select-',value:''}]);
            }
        };
        fetchDivisionList();
    }, [formData.company_id]);

    // Fetch division2 when division_id changes (matching PatientDetails.js)
    useEffect(() => {
        const fetchDivision2List = async () => {
            if (formData.division_id) {
                try {
                    let options = [{label:'-Select-',value:''}];
                    const res = await DivisionService.getDivisionByParentId(formData.division_id);
                    if (res && Array.isArray(res)) {
                        res.forEach((item) => {
                            options.push({
                                label: item.division_name,
                                value: item.division_id
                            });
                        });
                    }
                    setDivision2ListOptions(options);
                } catch (e) {
                    console.error('Error fetching division2:', e);
                }
            } else {
                setDivision2ListOptions([{label:'-Select-',value:''}]);
            }
        };
        fetchDivision2List();
    }, [formData.division_id]);

    // Define demographic fields organized by sections
    const demographicSections = [
        {
            title: "Company and Clinic Details",
            fields: [
                {
                    type: "SelectList",
                    label: "Company",
                    name: "company_id",
                    className: "form-control form-control-solid",
                    placeholder: "Company",
                    options: companyListOptions,
                    disabled: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Clinic",
                    name: "clinic_id",
                    className: "form-control form-control-solid",
                    placeholder: "Clinic",
                    options: clinicListOptions,
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Clinic Department",
                    name: "clinic_department_id",
                    className: "form-control form-control-solid",
                    placeholder: "Clinic Department",
                    options: clinicDepartmentListOptions,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Division",
                    name: "division_id",
                    className: "form-control form-control-solid",
                    placeholder: "Division",
                    options: divisionListOptions,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Division 2",
                    name: "division2_id",
                    className: "form-control form-control-solid",
                    placeholder: "Division 2",
                    options: division2ListOptions,
                    col: "col-12 col-md-6",
                },
            ]
        },
        {
            title: "Personal Information",
            fields: [
                {
                    type: "Checkbox",
                    label: "Is Dependent ?",
                    name: "is_dependent",
                    className: "form-check-input",
                    id: 'is_dependent',
                    col: "col-12",
                },
                {
                    type: "SelectList",
                    label: "Patient/Employee",
                    name: "dependent_id",
                    className: "form-control form-control-solid",
                    options: employeeListOptions,
                    placeholder: "Patient/Employee",
                    required: true,
                    isShow: formData.is_dependent == 1,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Dependent Relationship",
                    name: "dependent_relationship",
                    className: "form-control form-control-solid",
                    options: RelationShipOptions,
                    placeholder: "Dependent Relationship",
                    isShow: formData.is_dependent == 1,
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "First Name",
                    name: "first_name",
                    className: "form-control form-control-solid",
                    required: true,
                    isFirstLetterCaps: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Last Name",
                    name: "last_name",
                    className: "form-control form-control-solid",
                    required: true,
                    isFirstLetterCaps: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Gender",
                    name: "gender",
                    className: "form-control form-control-solid",
                    options: genderOptions,
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Date",
                    label: "Date of Birth",
                    name: "dob",
                    className: "form-control form-control-solid",
                    withAge: true,
                    disableFuture: true,
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Age",
                    label: "Age",
                    name: "dob",
                    className: "form-control form-control-solid",
                    readOnly: true,
                    isNotValidate: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Phone",
                    label: "Cell Number",
                    name: "phone",
                    className: "form-control form-control-solid",
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Email",
                    label: "Email Address",
                    name: "email",
                    className: "form-control form-control-solid",
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Ethnicity",
                    name: "ethnicity",
                    className: "form-control form-control-solid",
                    options: EthinicityOptions,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectSearchList",
                    label: "Race",
                    name: "race",
                    className: "form-control form-control-solid",
                    options: raceOptions,
                    isMulti: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Checkbox",
                    label: "Active",
                    name: "status",
                    className: "form-check-input",
                    id: 'status',
                    col: "col-12",
                },
            ]
        },
        // Employment Information - commented out
        // {
        //     title: "Employment Information",
        //     fields: [
        //         {
        //             type: "TextInput",
        //             label: "Job Title Description",
        //             name: "job_title_description",
        //             className: "form-control form-control-solid",
        //             placeholder: "Job Title Description",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "Date",
        //             label: "Start Date",
        //             name: "start_date",
        //             className: "form-control form-control-solid",
        //             placeholder: "Start Date",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "TextInput",
        //             label: "Employee Relationship",
        //             name: "employee_relationship",
        //             className: "form-control form-control-solid",
        //             placeholder: "Employee Relationship",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "TextInput",
        //             label: "Reporting Supervisor",
        //             name: "reporting_supervisor",
        //             className: "form-control form-control-solid",
        //             placeholder: "Reporting Supervisor",
        //             isFirstLetterCaps: true,
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "TextInput",
        //             label: "Benefit Type",
        //             name: "benefit_type",
        //             className: "form-control form-control-solid",
        //             placeholder: "Benefit Type",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "TextInput",
        //             label: "License/Certification Description",
        //             name: "license_certification_description",
        //             className: "form-control form-control-solid",
        //             placeholder: "License/Certification Description",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "Date",
        //             label: "License Effective Date",
        //             name: "license_effective_date",
        //             className: "form-control form-control-solid",
        //             placeholder: "License Effective Date",
        //             col: "col-12 col-md-6",
        //         },
        //         {
        //             type: "Date",
        //             label: "License Expiration Date",
        //             name: "license_expiration_date",
        //             className: "form-control form-control-solid",
        //             placeholder: "License Expiration Date",
        //             col: "col-12 col-md-6",
        //         },
        //     ]
        // },
        {
            title: "Address & Contact",
            fields: [
                {
                    type: "TextInput",
                    label: "Street Address",
                    name: "street_address",
                    className: "form-control form-control-solid",
                    placeholder: "Street",
                    required: true,
                    col: "col-12",
                },
                {
                    type: "TextInput",
                    label: "City",
                    name: "city",
                    className: "form-control form-control-solid",
                    placeholder: "City",
                    required: true,
                    isFirstLetterCaps: true,
                    col: "col-12 col-md-4",
                },
                {
                    type: "TextInput",
                    label: "State",
                    name: "state",
                    className: "form-control form-control-solid",
                    placeholder: "State",
                    required: true,
                    isFirstLetterCaps: true,
                    col: "col-12 col-md-4",
                },
                {
                    type: "TextInput",
                    label: "County",
                    name: "county",
                    className: "form-control form-control-solid",
                    placeholder: "County",
                    col: "col-12 col-md-4",
                },
                {
                    type: "ZipCode",
                    label: "Zipcode",
                    name: "zip_code",
                    className: "form-control form-control-solid",
                    placeholder: "Zipcode",
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Phone",
                    label: "Social Security Number",
                    name: "social_security_number",
                    className: "form-control form-control-solid",
                    placeholder: "Social Security Number",
                    numberFormat: SOCIAL_SECURITY_NUMBER_FORMAT,
                    maskChar: 'x',
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Full Name of Guardian & Parents",
                    name: "guardian_name",
                    className: "form-control form-control-solid",
                    placeholder: "Full Name of Guardian & Parents",
                    isFirstLetterCaps: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "Phone",
                    label: "Guardian Cell Number",
                    name: "guardian_phone",
                    className: "form-control form-control-solid",
                    placeholder: "Guardian Cell Number",
                    col: "col-12 col-md-6",
                },
            ]
        },
        {
            title: "Insurance Information",
            fields: [
                {
                    type: "SelectList",
                    label: "Insurance Name",
                    name: "insurance_id",
                    className: "form-control form-control-solid",
                    placeholder: "Insurance Name",
                    options: insuranceListOptions,
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Member Number",
                    name: "member_number",
                    className: "form-control form-control-solid",
                    placeholder: "Member Number",
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Group Number",
                    name: "group_number",
                    className: "form-control form-control-solid",
                    placeholder: "Group Number",
                    col: "col-12 col-md-6",
                },
            ]
        },
        {
            title: "General Information",
            fields: [
                {
                    type: "TextInput",
                    label: "Patient ID",
                    name: "patient_id",
                    className: "form-control form-control-solid",
                    placeholder: "Patient Id",
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Booking ID",
                    name: "booking_id",
                    className: "form-control form-control-solid",
                    placeholder: "Booking Id",
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Is Dot Driver?",
                    name: "is_dot_driver",
                    className: "form-control form-control-solid",
                    placeholder: "Is Dot Driver?",
                    options: isDotDriver,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "Identification Type",
                    name: "identification_type",
                    className: "form-control form-control-solid",
                    placeholder: "Identification type",
                    options: ItentificationTypeOptions,
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "TextInput",
                    label: "Identification Number",
                    name: "identification_number",
                    className: "form-control form-control-solid",
                    placeholder: "Identification number",
                    required: true,
                    col: "col-12 col-md-6",
                },
                {
                    type: "SelectList",
                    label: "License Issuing State",
                    name: "license_issuing_state",
                    className: "form-control form-control-solid",
                    placeholder: "License Issuing State",
                    options: stateOptions,
                    isShow: formData.identification_type === "Driver's License",
                    col: "col-12 col-md-6",
                },
                {
                    type: "Checkbox",
                    label: "Disable Labs / Drug Screen Notification",
                    name: "disable_labs_drugscreen_notification",
                    className: "form-check-input",
                    id: 'disable_labs_drugscreen_notification',
                    col: "col-12",
                },
            ]
        }
    ];

    // Scroll to first error section when validation errors appear
    useEffect(() => {
        if (validation && Object.keys(validation).length > 0) {
            // Find the first field with an error
            let firstErrorSection = null;
            
            // Check verification section first (most important)
            if (validation.demographics_verified) {
                setTimeout(() => {
                    if (verificationSectionRef.current) {
                        verificationSectionRef.current.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }
                }, 100);
                return;
            }
            
            // Check all sections for errors
            demographicSections.forEach((section, sectionIndex) => {
                if (firstErrorSection !== null) return; // Already found first error
                
                const hasErrorInSection = section.fields.some(field => {
                    if (isSet(field.isShow, true) === false) return false;
                    return isSet(validation[field.name], '') !== '';
                });
                
                if (hasErrorInSection) {
                    firstErrorSection = sectionIndex;
                }
            });
            
            // Scroll to the first error section
            if (firstErrorSection !== null && sectionRefs.current[firstErrorSection]) {
                setTimeout(() => {
                    sectionRefs.current[firstErrorSection].scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }, 100);
            }
        }
    }, [validation]);

    return (
        <div className="demographics-verification-container">
            <style>{`
                .demographics-verification-container {
                    padding: 0;
                }
                
                .demographics-header {
                    padding: 1rem 0;
                    margin: 0 0 1.5rem 0;
                    text-align: center;
                    display: block;
                }
                
                .demographics-header h4 {
                    font-size: 1.5rem;
                    font-weight: 700;
                    margin-bottom: 0;
                    color: #1f2937;
                }
                
                .demographics-header p {
                    font-size: 0.95rem;
                    opacity: 0.95;
                    margin: 0;
                    color: white;
                }
                
                .section-card {
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 1.5rem;
                    margin-bottom: 1.5rem;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    transition: box-shadow 0.3s ease;
                }
                
                .section-card:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                
                .section-header {
                    margin-bottom: 1.5rem;
                    padding-bottom: 1rem;
                    border-bottom: 2px solid #f3f4f6;
                }
                
                .section-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin: 0;
                }
                
                .form-group-mobile {
                    margin-bottom: 1.25rem;
                }
                
                .form-group-mobile label {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #374151;
                    margin-bottom: 0.5rem;
                    display: block;
                }
                
                .form-group-mobile .form-control {
                    border-radius: 8px;
                    border: 1px solid #d1d5db;
                    padding: 0.50rem;
                    font-size: 0.95rem;
                    transition: all 0.2s ease;
                }
                
                .form-group-mobile .form-control:focus {
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                    outline: none;
                }
                
                .verification-section {
                    padding: 1.5rem 0;
                    margin-top: 2rem;
                    border-top: 2px solid #e5e7eb;
                    padding-top: 2rem;
                }
                
                .verification-title {
                    font-size: 1.25rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 1.25rem;
                }
                
                .verification-checkbox-wrapper {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.875rem;
                    margin-top: 0;
                    padding: 1rem;
                    background: #f9fafb;
                    border-radius: 8px;
                    border: 1px solid #e5e7eb;
                }
                
                .verification-checkbox-wrapper input[type="checkbox"] {
                    width: 22px;
                    height: 22px;
                    min-width: 22px;
                    cursor: pointer;
                    accent-color: #0ea5e9;
                    flex-shrink: 0;
                    margin-top: 2px;
                }
                
                .verification-label {
                    font-size: 1rem;
                    font-weight: 600;
                    color: #1f2937;
                    line-height: 1.6;
                    flex: 1;
                    margin: 0;
                    cursor: pointer;
                }
                
                .verification-label-mobile {
                    display: none;
                }
                
                .verification-error {
                    color: #dc2626;
                    font-size: 0.875rem;
                    margin-top: 0.5rem;
                    text-align: center;
                }
                
                .info-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 0.5rem;
                    background: #fef3c7;
                    color: #92400e;
                    padding: 0.5rem 1rem;
                    border-radius: 8px;
                    font-size: 0.875rem;
                    margin-bottom: 1.5rem;
                }
                
                @media (max-width: 768px) {
                    .demographics-header {
                        padding: 0.75rem 0;
                        margin: 0 0 1.25rem 0;
                    }
                    
                    .demographics-header h4 {
                        font-size: 1.25rem;
                        font-weight: 700;
                    }
                    
                    .section-card {
                        padding: 1.25rem;
                        margin-bottom: 1.25rem;
                    }
                    
                    .section-header {
                        margin-bottom: 1.25rem;
                    }
                    
                    .section-title {
                        font-size: 1.1rem;
                    }
                    
                    .verification-section {
                        padding: 1rem 0;
                        padding-top: 1.5rem;
                    }
                    
                    .verification-title {
                        font-size: 1.1rem;
                        font-weight: 700;
                        margin-bottom: 1rem;
                    }
                    
                    .verification-checkbox-wrapper {
                        padding: 0.875rem;
                    }
                    
                    .verification-label {
                        display: none;
                    }
                    
                    .verification-label-mobile {
                        display: block;
                        font-size: 0.9rem;
                        font-weight: 600;
                        color: #1f2937;
                        line-height: 1.5;
                        margin: 0;
                        cursor: pointer;
                    }
                    
                    .verification-checkbox-wrapper input[type="checkbox"] {
                        width: 20px;
                        height: 20px;
                        min-width: 20px;
                        margin-top: 2px;
                    }
                    
                    .form-group-mobile {
                        margin-bottom: 1rem;
                    }
                }
                
                @media (max-width: 480px) {
                    .demographics-header h4 {
                        font-size: 1.1rem;
                        font-weight: 700;
                    }
                    
                    .section-card {
                        padding: 1rem;
                    }
                    
                    .verification-section {
                        padding: 0.75rem 0;
                        padding-top: 1.25rem;
                    }
                    
                    .verification-title {
                        font-size: 1rem;
                        font-weight: 700;
                        margin-bottom: 0.875rem;
                    }
                    
                    .verification-checkbox-wrapper {
                        padding: 0.75rem;
                    }
                    
                    .verification-label-mobile {
                        font-size: 0.85rem;
                        line-height: 1.4;
                    }
                    
                    .verification-checkbox-wrapper input[type="checkbox"] {
                        width: 18px;
                        height: 18px;
                        min-width: 18px;
                    }
                }
            `}</style>

            <div className="demographics-header">
                <h4>Demographics Verification</h4>
            </div>

            <div className="info-badge">
                <span>ℹ️</span>
                <span>Please review all information carefully. Make any necessary corrections before verifying.</span>
            </div>

            {demographicSections.map((section, sectionIndex) => (
                <div 
                    key={sectionIndex} 
                    className="section-card"
                    ref={(el) => (sectionRefs.current[sectionIndex] = el)}
                >
                    <div className="section-header">
                        <h5 className="section-title">{section.title}</h5>
                    </div>
                    <div className="row">
                        {section.fields.map((field, fieldIndex) => {
                            if (isSet(field.isShow, true) === false) { return null; }
                            const fieldValue = isSet(formData[field.name], '');
                            const hasError = isSet(validation[field.name], '') !== '';
                            
                            // Handle checkbox fields differently
                            if (field.type === "Checkbox") {
                                return (
                                    <div key={fieldIndex} className={`${field.col || 'col-12'} form-group-mobile`}>
                                        <div className="form-check">
                                            <FormInputs
                                                {...field}
                                                value={formData[field.name] || 0}
                                                changeHandler={(value, e) => onChangeHandler(field.name, value, e)}
                                            />
                                            <label className="form-check-label" htmlFor={field.id || field.name}>
                                                {field.label}
                                                {field.required && <span className="text-danger ml-1">*</span>}
                                            </label>
                                        </div>
                                        {hasError && (
                                            <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                                                {validation[field.name]}
                                            </div>
                                        )}
                                    </div>
                                );
                            }
                            
                            return (
                                <div key={fieldIndex} className={`${field.col || 'col-12'} form-group-mobile`}>
                                    <label className="mb-2">
                                        {field.label}
                                        {field.required && <span className="text-danger ml-1">*</span>}
                                    </label>
                                    <FormInputs
                                        {...field}
                                        value={fieldValue}
                                        changeHandler={(value, e) => onChangeHandler(field.name, value, e)}
                                    />
                                    {hasError && (
                                        <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                                            {validation[field.name]}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}

            <div 
                className="verification-section"
                ref={verificationSectionRef}
            >
                <h5 className="verification-title">
                    Verification Required
                </h5>
                <div className="verification-checkbox-wrapper">
                    <input
                        type="checkbox"
                        id="demographics_verified"
                        name="demographics_verified"
                        checked={formData.demographics_verified === true || formData.demographics_verified === 1}
                        onChange={(e) => onChangeHandler('demographics_verified', e.target.checked ? 1 : 0)}
                        required
                    />
                    <label htmlFor="demographics_verified" className="verification-label">
                        I verify that all the information provided above is accurate and correct to the best of my knowledge.
                    </label>
                    <label htmlFor="demographics_verified" className="verification-label-mobile">
                        I verify all information above is accurate and correct.
                    </label>
                </div>
                {isSet(validation.demographics_verified, '') !== '' && (
                    <div className="verification-error">
                        {validation.demographics_verified}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DemographicsVerification;

